/**
 * "New document" dialog for Document Studio.
 *
 * Captures a collection path (reusing the open bar's live + curated autocomplete)
 * and a document id, then asks the studio to create a document at
 * `<collection>/<id>`. Typing a brand-new collection segment is how a new
 * sub-collection is made — Firestore has no separate "create collection" step.
 *
 * Suggestions include both collections and existing documents: composing a deep
 * path (e.g. `coll/existingDoc/subcoll`) means drilling through the intermediate
 * document too, so every pick drills in (appends "/") rather than opening. The
 * final odd-segment collection check is enforced at create time.
 */

import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../../shared/enums/modalId";
import { usePathSuggestions } from "../hooks/usePathSuggestions";
import { isCollectionPath, normalizePath } from "../utils/documentSource";

interface CreateResult {
  ok: boolean;
  existed: boolean;
  error: string | null;
}

interface CreateDocumentModalProps {
  isOpen: boolean;
  /** Collection to prefill (e.g. the parent of the currently open document). */
  defaultCollectionPath: string;
  onCreate: (collectionPath: string, docId: string) => Promise<CreateResult>;
  onClose: () => void;
}

const fieldClass =
  "w-full rounded-md border border-primaryGray-15 px-3 py-2 font-mono text-sm outline-none focus:border-tertiaryBlue-4";

const CreateDocumentModal = ({
  isOpen,
  defaultCollectionPath,
  onCreate,
  onClose,
}: CreateDocumentModalProps) => {
  const dialogId = ModalId.DOCUMENT_STUDIO_CREATE_MODAL;
  const isModalOpenRef = useRef(false);

  const [collectionPath, setCollectionPath] = useState("");
  const [docId, setDocId] = useState("");
  const [creating, setCreating] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  // Suggest both collections and existing documents: a deep create path drills
  // through intermediate document ids too (e.g. coll/existingDoc/subcoll), so
  // every pick drills in via `drillInto`. The final odd-segment collection check
  // is enforced at create time, not by filtering suggestions here.
  const suggestions = usePathSuggestions(collectionPath);

  const normalizedCollection = normalizePath(collectionPath);
  const trimmedId = docId.trim();
  const collectionValid = isCollectionPath(normalizedCollection);
  const idValid = trimmedId !== "" && !trimmedId.includes("/");
  const fullPath =
    collectionValid && idValid ? `${normalizedCollection}/${trimmedId}` : "";
  const canCreate = collectionValid && idValid && !creating;
  const dropdownOpen = showSuggestions && suggestions.length > 0;

  // Sync the imperative kit modal with the declarative isOpen prop, and reset the
  // form each time it opens so a stale draft never leaks across sessions.
  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      setCollectionPath(defaultCollectionPath);
      setDocId("");
      setLocalError(null);
      setHighlight(-1);
      openModal(dialogId);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(dialogId);
      isModalOpenRef.current = false;
    }
  }, [isOpen, dialogId, defaultCollectionPath]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        closeModal(dialogId);
      }
    },
    [dialogId],
  );

  const previewLabel = useMemo(() => {
    if (fullPath) {
      return `Will create: ${fullPath}`;
    }
    if (!collectionValid) {
      return "Enter a collection path (odd number of segments), e.g. configurations.";
    }
    return "Enter a document id.";
  }, [fullPath, collectionValid]);

  const changeCollection = (next: string) => {
    setCollectionPath(next);
    setHighlight(-1);
    setShowSuggestions(true);
    setLocalError(null);
  };

  const drillInto = (value: string) => {
    setCollectionPath(`${value}/`);
    setHighlight(-1);
    setShowSuggestions(true);
  };

  const onCollectionKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setShowSuggestions(true);
      setHighlight((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, -1));
    } else if (event.key === "Enter") {
      if (dropdownOpen && highlight >= 0 && suggestions[highlight]) {
        event.preventDefault();
        drillInto(suggestions[highlight].value);
      }
    } else if (event.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleCreate = async () => {
    if (!canCreate) {
      return;
    }
    setCreating(true);
    setLocalError(null);
    const result = await onCreate(normalizedCollection, trimmedId);
    setCreating(false);
    if (!result.ok) {
      setLocalError(result.error ?? "Could not create document.");
    }
  };

  return (
    <Modal
      dialogId={dialogId}
      title="New document"
      saveButtonText={creating ? "Creating..." : "Create"}
      cancelText="Cancel"
      onSave={handleCreate}
      onCancel={onClose}
      onClose={onClose}
      disableSave={!canCreate}
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col gap-1">
          <label className="text-tiny font-semibold text-primaryGray-2">
            Collection path
          </label>
          <input
            type="text"
            value={collectionPath}
            onChange={(event) => changeCollection(event.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setShowSuggestions(false)}
            onKeyDown={onCollectionKeyDown}
            placeholder="e.g. configurations or configurations/payer_guidelines/humana"
            spellCheck={false}
            autoComplete="off"
            className={fieldClass}
          />
          {dropdownOpen && (
            <ul className="mt-1 max-h-60 overflow-y-auto rounded-md border border-primaryGray-15 bg-white py-1">
              {suggestions.map((suggestion, index) => (
                <li key={suggestion.value}>
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setHighlight(index)}
                    onClick={() => drillInto(suggestion.value)}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
                      index === highlight
                        ? "bg-tertiaryBlue-12"
                        : "hover:bg-primaryGray-16"
                    }`}
                  >
                    <span className="shrink-0 font-mono text-sm text-primaryGray-2">
                      {suggestion.label}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-tiny text-primaryGray-8">
                      {suggestion.detail}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-tiny font-semibold text-primaryGray-2">
            Document id
          </label>
          <input
            type="text"
            value={docId}
            onChange={(event) => {
              setDocId(event.target.value);
              setLocalError(null);
            }}
            placeholder="e.g. default"
            spellCheck={false}
            autoComplete="off"
            className={fieldClass}
          />
        </div>

        <span className="font-mono text-tiny text-primaryGray-8">
          {previewLabel}
        </span>
        {localError && (
          <span className="text-tiny font-medium text-tertiaryRed-4">
            {localError}
          </span>
        )}
      </div>
    </Modal>
  );
};

export default CreateDocumentModal;
